import { useMemo, useCallback } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useCurrentWeekMealPlan, useFavoriteCount, useRandomRecipes } from '@/hooks'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  LogOut,
  Calendar,
  ShoppingCart,
  Heart,
  Search,
  ChefHat,
  UtensilsCrossed,
  Sparkles,
  ArrowRight,
  RefreshCw,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MealPlanItem, Recipe } from '@/types/api'
import useEmblaCarousel from 'embla-carousel-react'
import { useQueryClient } from '@tanstack/react-query'
import { recipesKeys } from '@/hooks'

// ─── Animation variants ────────────────────────────────────────
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.07 },
  },
}

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
}

// ─── Helpers ────────────────────────────────────────────────────
function getGreeting(): string {
  const h = new Date().getHours()
  if (h < 7) return 'Buenas noches'
  if (h < 12) return 'Buenos días'
  if (h < 20) return 'Buenas tardes'
  return 'Buenas noches'
}

function getTodayFormatted(): string {
  return new Date().toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

/** Compare dates in local timezone to avoid UTC offset issues */
function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function getTodayMeals(items: MealPlanItem[]): {
  comida: MealPlanItem | undefined
  cena: MealPlanItem | undefined
} {
  const todayStr = toLocalDateStr(new Date())
  const todayItems = items.filter((item) => toLocalDateStr(new Date(item.day)) === todayStr)
  return {
    comida: todayItems.find((i) => i.service === 'comida'),
    cena: todayItems.find((i) => i.service === 'cena'),
  }
}

// ─── Component ──────────────────────────────────────────────────
export function DashboardPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const { data: mealPlan, isLoading: mealPlanLoading } = useCurrentWeekMealPlan()
  const { data: favoriteCount } = useFavoriteCount()
  const { data: randomRecipes, isLoading: recipesLoading } = useRandomRecipes(8)
  const queryClient = useQueryClient()

  const refreshDiscover = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: recipesKeys.random(8) })
  }, [queryClient])

  const todayMeals = useMemo(
    () => (mealPlan ? getTodayMeals(mealPlan.items) : { comida: undefined, cena: undefined }),
    [mealPlan],
  )

  const hasTodayMeals = todayMeals.comida || todayMeals.cena
  const totalPlannedThisWeek = mealPlan?.items.length ?? 0

  return (
    <>
      {/* Mobile-only compact top bar (sidebar handles desktop) */}
      <header className="bg-white border-b border-gray-200 lg:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <ChefHat className="h-5 w-5 text-primary" />
            <span className="font-bold text-gray-900">MyMeals</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => logout()} className="text-gray-500">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <motion.div
        className="max-w-2xl mx-auto px-4 py-6 space-y-6"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* ── Greeting ─────────────────────────────── */}
        <motion.div variants={fadeUp}>
          <p className="text-sm text-gray-500 capitalize">{getTodayFormatted()}</p>
          <h1 className="text-2xl font-bold text-gray-900 mt-0.5">
            {getGreeting()}
            {user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
        </motion.div>

        {/* ── Today's Meals Card ───────────────────── */}
        <motion.div variants={fadeUp}>
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-5 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <UtensilsCrossed className="h-4 w-4 text-primary" />
                Hoy en tu menú
              </h2>
            </div>
            <CardContent className="p-0">
              {mealPlanLoading ? (
                <div className="p-5 space-y-3">
                  <div className="h-12 bg-gray-100 rounded-lg animate-pulse" />
                  <div className="h-12 bg-gray-100 rounded-lg animate-pulse" />
                </div>
              ) : hasTodayMeals ? (
                <div className="divide-y divide-gray-100">
                  {/* Comida */}
                  <TodayMealRow
                    label="Comida"
                    item={todayMeals.comida}
                    onNavigate={(id) => navigate(`/recipes/${id}`)}
                  />
                  {/* Cena */}
                  <TodayMealRow
                    label="Cena"
                    item={todayMeals.cena}
                    onNavigate={(id) => navigate(`/recipes/${id}`)}
                  />
                </div>
              ) : (
                <div className="p-6 text-center">
                  <Sparkles className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 mb-3">
                    No tienes comidas planificadas para hoy
                  </p>
                  <Button size="sm" variant="outline" onClick={() => navigate('/meal-planner')}>
                    <Calendar className="h-4 w-4 mr-1.5" />
                    Planificar
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* ── Weekly Progress ──────────────────────── */}
        <motion.div variants={fadeUp}>
          <WeekDots items={mealPlan?.items ?? []} />
        </motion.div>

        {/* ── Quick Actions 2×2 ────────────────────── */}
        <motion.div variants={fadeUp}>
          <div className="grid grid-cols-2 gap-3">
            <QuickAction
              icon={Search}
              label="Descubrir"
              sub="Buscar recetas"
              onClick={() => navigate('/recipes')}
            />
            <QuickAction
              icon={Calendar}
              label="Planificar"
              sub={`${totalPlannedThisWeek} esta semana`}
              onClick={() => navigate('/meal-planner')}
            />
            <QuickAction
              icon={ShoppingCart}
              label="Compras"
              sub="Lista automática"
              onClick={() => navigate('/shopping-list')}
            />
            <QuickAction
              icon={Heart}
              label="Favoritos"
              sub={favoriteCount != null ? `${favoriteCount} guardadas` : '—'}
              onClick={() => navigate('/favorites')}
              iconClassName="text-red-500"
            />
          </div>
        </motion.div>

        {/* ── Discover Carousel ────────────────────── */}
        <motion.div variants={fadeUp}>
          <DiscoverCarousel
            recipes={randomRecipes ?? []}
            isLoading={recipesLoading}
            onRefresh={refreshDiscover}
            onRecipeClick={(id) => navigate(`/recipes/${id}`)}
          />
        </motion.div>
      </motion.div>
    </>
  )
}

// ─── Sub-components ─────────────────────────────────────────────

function TodayMealRow({
  label,
  item,
  onNavigate,
}: {
  label: string
  item: MealPlanItem | undefined
  onNavigate: (id: string) => void
}) {
  if (!item) {
    return (
      <div className="flex items-center gap-3 px-5 py-3.5 text-gray-400">
        <span className="text-xs font-medium uppercase w-14">{label}</span>
        <span className="text-sm italic">Sin planificar</span>
      </div>
    )
  }

  return (
    <button
      className="flex items-center gap-3 px-5 py-3.5 w-full text-left hover:bg-gray-50 
                 transition-colors active:bg-gray-100 touch-manipulation"
      onClick={() => onNavigate(item.recipe.id)}
    >
      {item.recipe.imageUrl ? (
        <img
          src={item.recipe.imageUrl}
          alt={item.recipe.title}
          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
          <UtensilsCrossed className="h-4 w-4 text-gray-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <span className="text-[10px] font-medium uppercase text-gray-400">{label}</span>
        <p className="text-sm font-medium text-gray-900 truncate">{item.recipe.title}</p>
      </div>
      <ArrowRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
    </button>
  )
}

function QuickAction({
  icon: Icon,
  label,
  sub,
  onClick,
  iconClassName,
}: {
  icon: React.ElementType
  label: string
  sub: string
  onClick: () => void
  iconClassName?: string
}) {
  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-all active:scale-[0.97] touch-manipulation"
      onClick={onClick}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
          <Icon className={`h-5 w-5 ${iconClassName ?? 'text-primary'}`} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{label}</p>
          <p className="text-[11px] text-gray-500 truncate">{sub}</p>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Discover Carousel ──────────────────────────────────────────

function DiscoverCarousel({
  recipes,
  isLoading,
  onRefresh,
  onRecipeClick,
}: {
  recipes: Recipe[]
  isLoading: boolean
  onRefresh: () => void
  onRecipeClick: (id: string) => void
}) {
  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-500" />
          Descubre algo nuevo
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
          className="h-7 px-2 text-gray-400 hover:text-gray-600"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {isLoading ? (
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex-shrink-0 w-36 h-44 rounded-xl bg-gray-100 animate-pulse"
            />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden -mx-4 px-4" ref={emblaRef}>
          <div className="flex gap-3">
            {recipes.map((recipe) => (
              <button
                key={recipe.id}
                className="flex-shrink-0 w-36 group touch-manipulation"
                onClick={() => onRecipeClick(recipe.id)}
              >
                <div className="relative h-44 rounded-xl overflow-hidden bg-gray-100">
                  {recipe.imageUrl ? (
                    <img
                      src={recipe.imageUrl}
                      alt={recipe.title}
                      className="w-full h-full object-cover transition-transform duration-300 
                                 group-hover:scale-105 group-active:scale-100"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <UtensilsCrossed className="h-8 w-8 text-gray-300" />
                    </div>
                  )}
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  {/* Title */}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-white text-xs font-medium leading-tight line-clamp-2">
                      {recipe.title}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Week Dots ──────────────────────────────────────────────────
const DAY_LABELS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']

function WeekDots({ items }: { items: MealPlanItem[] }) {
  const today = new Date()
  const todayIdx = ((today.getDay() + 6) % 7) // Monday=0

  // Compute which days of the current week have meals (local timezone)
  const filledDays = useMemo(() => {
    const set = new Set<number>()
    // Monday of the current week in local time
    const weekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - todayIdx)

    items.forEach((item) => {
      const d = new Date(item.day)
      // Compare using local date only (ignore time/tz)
      const localItem = new Date(d.getFullYear(), d.getMonth(), d.getDate())
      const diff = Math.round(
        (localItem.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24),
      )
      if (diff >= 0 && diff < 7) set.add(diff)
    })
    return set
  }, [items, todayIdx, today])

  return (
    <div className="flex items-center justify-between px-2">
      {DAY_LABELS.map((label, i) => {
        const isToday = i === todayIdx
        const filled = filledDays.has(i)

        return (
          <div key={label} className="flex flex-col items-center gap-1.5">
            <span
              className={`text-[10px] font-medium ${
                isToday ? 'text-primary' : 'text-gray-400'
              }`}
            >
              {label}
            </span>
            <div
              className={`w-3 h-3 rounded-full transition-colors ${
                filled
                  ? 'bg-primary'
                  : isToday
                    ? 'border-2 border-primary bg-transparent'
                    : 'bg-gray-200'
              }`}
            />
          </div>
        )
      })}
    </div>
  )
}
